/**
 * FormProvider Component
 *
 * A wrapper around Formik that provides a library-agnostic form context.
 * Handles form state, validation, and submission.
 *
 * @example
 * ```typescript
 * import { FormProvider, v } from '@/forms/core';
 *
 * const schema = v.object().shape({
 *   email: v.string().email().required(),
 * });
 *
 * const MyForm = () => (
 *   <FormProvider
 *     initialValues={{ email: '' }}
 *     validationSchema={schema.build()}
 *     onSubmit={(values) => console.log(values)}
 *   >
 *     <MyInputs />
 *   </FormProvider>
 * );
 *
 * // Headless mode for custom form rendering
 * const CustomForm = () => (
 *   <FormProvider
 *     headless
 *     initialValues={{ email: '' }}
 *     validationSchema={schema.build()}
 *     onSubmit={(values) => console.log(values)}
 *   >
 *     {({ handleSubmit, values, errors }) => (
 *       <form onSubmit={handleSubmit}>
 *         <MyInputs />
 *       </form>
 *     )}
 *   </FormProvider>
 * );
 * ```
 */

import React from 'react';
import { Formik, Form } from 'formik';
import type {
  FormProviderProps,
  FormHelpers,
  FormRenderContext,
} from '../../types';
import { ValidationAdapter } from '../../validation';
import type { ObjectSchema } from '../../validation';

/**
 * Formik's enableReinitialize watcher compares its `initialTouched` ref (which defaults to
 * `{}`) against the prop; left undefined the two never compare equal, so it dispatches one
 * SET_TOUCHED right after mount and silently drops anything touched during that first commit.
 * A stable empty object makes the comparison hold. Module-level so the identity never changes.
 */
const NO_INITIAL_TOUCHED = {};

/**
 * Build the render context from Formik props
 */
function buildRenderContext<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formikProps: any,
): FormRenderContext<T> {
  return {
    // State
    values: formikProps.values,
    errors: formikProps.errors as Record<string, string | undefined>,
    touched: formikProps.touched as Record<string, boolean>,
    dirty: formikProps.dirty,
    isValid: formikProps.isValid,
    isSubmitting: formikProps.isSubmitting,
    isValidating: formikProps.isValidating,
    submitCount: formikProps.submitCount,
    // Actions
    setFieldValue: formikProps.setFieldValue,
    setFieldTouched: formikProps.setFieldTouched,
    setFieldError: formikProps.setFieldError,
    setValues: formikProps.setValues,
    setErrors: formikProps.setErrors,
    setTouched: formikProps.setTouched,
    validateField: formikProps.validateField,
    validateForm: formikProps.validateForm as (
      values?: unknown,
    ) => Promise<Record<string, string>>,
    submitForm: formikProps.submitForm,
    resetForm: formikProps.resetForm,
    // Form handler
    handleSubmit: formikProps.handleSubmit,
  };
}

/**
 * Form provider component that wraps Formik
 */
export function FormProvider<T extends Record<string, unknown>>({
  initialValues,
  validationSchema,
  onSubmit,
  enableReinitialize = false,
  onReset,
  validateOnChange = true,
  validateOnBlur = true,
  children,
  className,
  style,
  autoComplete = 'off',
  headless = false,
}: FormProviderProps<T>) {
  // Convert our schema to Yup if it's our ObjectSchema format
  const yupSchema = React.useMemo(() => {
    if (!validationSchema) return undefined;

    // Check if it's already a Yup schema (has 'validate' method)
    if (typeof validationSchema.validate === 'function') {
      return validationSchema;
    }

    // Check if it's our ObjectSchema format (has 'fields' property)
    if ('fields' in validationSchema) {
      return ValidationAdapter.toLibrarySchema(
        validationSchema as ObjectSchema,
      );
    }

    // Otherwise assume it's already a valid schema
    return validationSchema;
  }, [validationSchema]);

  return (
    <Formik
      initialValues={initialValues}
      initialTouched={NO_INITIAL_TOUCHED}
      validationSchema={yupSchema}
      onSubmit={(values, formikHelpers) => {
        const helpers: FormHelpers = {
          setSubmitting: formikHelpers.setSubmitting,
          setErrors: formikHelpers.setErrors,
          setValues: formikHelpers.setValues,
          resetForm: formikHelpers.resetForm,
        };
        return onSubmit(values, helpers);
      }}
      enableReinitialize={enableReinitialize}
      onReset={onReset}
      validateOnChange={validateOnChange}
      validateOnBlur={validateOnBlur}
    >
      {formikProps => {
        const context = buildRenderContext<T>(formikProps);

        // Headless mode - don't render Form, let consumer handle it
        if (headless) {
          return typeof children === 'function' ? children(context) : children;
        }

        // Standard mode - render Form with content
        return (
          <Form className={className} style={style} autoComplete={autoComplete}>
            {typeof children === 'function' ? children(context) : children}
          </Form>
        );
      }}
    </Formik>
  );
}
